import React, { useState, useEffect } from 'react';
import { Sparkles, Trophy, X, Gift } from 'lucide-react';

const SurpriseDropModal = ({ drop, onClose }) => {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (drop) {
      const timer = setTimeout(() => setRevealed(true), 800);
      return () => clearTimeout(timer);
    }
  }, [drop]);

  if (!drop) return null;

  const rarityColors = {
    common: 'from-gray-400 to-gray-500',
    rare: 'from-blue-500 to-indigo-600',
    epic: 'from-purple-500 to-pink-500',
    legendary: 'from-amber-400 to-orange-500',
  };

  const gradient = rarityColors[drop.rarity] || rarityColors.rare;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="surprise-drop-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Gradient Header */}
        <div className={`bg-gradient-to-r ${gradient} p-6 text-center relative`}>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1 text-white/70 hover:text-white rounded-full"
            data-testid="surprise-drop-close"
          >
            <X size={18} />
          </button>
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm mb-3 ${
            revealed ? 'animate-bounce' : 'animate-pulse'
          }`}>
            {revealed ? <Trophy size={32} className="text-white" /> : <Gift size={32} className="text-white" />}
          </div>
          <h2 className="text-xl font-bold text-white">
            {revealed ? 'Surprise Drop!' : 'You earned something...'}
          </h2>
          <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-xs font-bold text-white uppercase tracking-wider">
            {drop.rarity}
          </span>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          {revealed ? (
            <>
              <h3 className="text-lg font-bold text-gray-900 mb-1">{drop.title}</h3>
              <p className="text-sm text-gray-600 mb-4">{drop.description}</p>
              {drop.image_url && (
                <div className="w-32 h-32 mx-auto mb-4 rounded-xl overflow-hidden shadow-lg">
                  <img src={drop.image_url} alt={drop.title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex items-center justify-center gap-1.5 text-amber-600 text-sm font-medium">
                <Sparkles size={14} />
                <span>Added to your collection</span>
              </div>
            </>
          ) : (
            <div className="py-4">
              <div className="flex items-center justify-center gap-2 text-gray-400">
                <Sparkles size={16} className="animate-spin" />
                <span className="text-sm">Revealing...</span>
              </div>
            </div>
          )}
        </div>

        {revealed && (
          <div className="px-6 pb-6">
            <button
              onClick={onClose}
              className={`w-full py-3 bg-gradient-to-r ${gradient} text-white font-semibold rounded-xl hover:shadow-lg transition-all`}
              data-testid="surprise-drop-collect"
            >
              Collect
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SurpriseDropModal;
